import { useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import type { PaperListResponse, PaperWithRole } from "@/types/paper";

export function useReadingStatus(conceptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { paperId: string; status: string; source?: "auto" }) =>
      apiClient
        .patch(`api/concepts/${conceptId}/papers/${data.paperId}`, {
          json: {
            reading_status: data.status,
            ...(data.source ? { reading_status_source: data.source } : {}),
          },
        })
        .json<PaperWithRole>(),
    onMutate: async (data) => {
      // Auto-promotions are server-arbitrated ("client decides WHEN, server
      // decides WHETHER" — see useReadingAutopromote). Applying them
      // optimistically flips reading_status, which the server may then reject;
      // the onSettled refetch reverts it, re-arming the auto-promote eligibility
      // and producing an optimistic→revert→re-fire loop that oscillates the
      // reviewed count (and any UI keyed on it). Only apply optimistic updates
      // for MANUAL changes, which the server always accepts.
      if (data.source === "auto") return { previous: undefined };
      // Cancel in-flight fetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ["papers", conceptId] });
      // Snapshot previous value for rollback
      const previous = queryClient.getQueriesData<PaperListResponse>({
        queryKey: ["papers", conceptId],
      });
      // Optimistic update
      queryClient.setQueriesData<PaperListResponse>(
        { queryKey: ["papers", conceptId] },
        (old) =>
          old
            ? {
                ...old,
                papers: old.papers.map((p) =>
                  p.id === data.paperId
                    ? {
                        ...p,
                        reading_status: data.status,
                        reading_status_source: data.source ?? "manual",
                      }
                    : p,
                ),
              }
            : old,
      );
      return { previous };
    },
    onError: (_err, _data, context) => {
      // Rollback on failure
      if (context?.previous) {
        for (const [key, data] of context.previous) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      // Refetch to ensure server state is synced
      queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
    },
  });
}
